import { PREFIXE_MALI } from "@hakili/domain-types";
import { t } from "@hakili/i18n";
import { BOUTON_TEXTE, Button, Card } from "@hakili/ui";
import { useState } from "react";

/**
 * `P1-CLW-008`: WF-CLI-02, `Connexion par code et récupération`.
 *
 * **The masked number is the workflow's, and it is a privacy rule.** Step 1:
 * *"masked phone (all digits except the last two replaced by `x`)"*. A handset
 * left on a table shows whose account it is to anybody who picks it up unless
 * the number is masked, and the last two digits are what let the owner
 * recognise their own.
 *
 * **The lockout sentence is the SERVER's.** WF-CLI-02's `DECISION ADDED`: *"the
 * lockout must be enforced server-side per client and per device, not only in
 * the UI, with a 15 minute cooldown."* So this screen counts nothing and decides
 * nothing , it renders the refusal the API returned, which is
 * `PIN_LOCKED_MESSAGE` at the third failure and `Code incorrect. Il vous reste N
 * essai(s).` before it. A screen that kept its own counter would show a person
 * two attempts remaining after they had exhausted them on another tab.
 */

const CHAMP = "w-full rounded-xl border border-gray-200 px-3 py-3 text-base";

/**
 * All digits but the last two, per WF-CLI-02 step 1 , **and the country code
 * stays**, per the founder's decision D2 of 3 September 2026.
 *
 * 06-cross-cutting 6.1 now reads *"the masked display keeps the country code and
 * the last two digits"*, and the copy the record writes is
 * `Envoyé sur WhatsApp au +223 xx xx xx xx`. The country code is not a secret ,
 * a handset on a table reveals nothing by admitting it is Malian , while the
 * subscriber digits are the ones that identify a person. Hiding the code as well
 * made the mask *look* safer and told its owner less: the last two digits let
 * you recognise your own number, and the country code is how you notice you are
 * looking at the wrong one.
 *
 * **The indicatif is passed in, never guessed.** Knowing where `+1` ends and the
 * national number begins is per-country parsing, which is `P0-IDN-051`'s domain
 * half. The screen does not guess it because the screen does not have to: the
 * person chose it. When a number does not carry the indicatif it was rendered
 * with , a restored session, until `P0-IDN-051` lands , only the leading `+` is
 * kept, which is the honest floor rather than a wrong country code.
 */
export function masque(phone: string, indicatif: string = PREFIXE_MALI): string {
  if (phone.length <= 2) return phone;
  const fin = phone.slice(-2);
  const garde = phone.startsWith(indicatif) ? indicatif : phone.startsWith("+") ? "+" : "";
  const milieu = phone.slice(garde.length, -2).replaceAll(/\d/gu, "x");
  return `${garde}${milieu}${fin}`;
}

export function Connexion({
  phone,
  connecter,
  oublie,
  pasVous,
  refusal,
}: {
  readonly phone: string;
  readonly connecter: (pin: string) => Promise<void>;
  readonly oublie: () => void;
  readonly pasVous: () => void;
  readonly refusal?: string | undefined;
}): React.JSX.Element {
  const [pin, setPin] = useState("");
  const [enCours, setEnCours] = useState(false);

  const soumettre = (): void => {
    if (pin.length !== 4 || enCours) return;
    setEnCours(true);
    void connecter(pin).finally(() => {
      setEnCours(false);
    });
  };

  return (
    <Card shape="client" className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-gray-900">{t("client.connexion.titre")}</h1>
      <p className="text-sm text-gray-500">{masque(phone)}</p>
      {/**
       * **A real form, and the buyer's own gesture is why.** Chrome said it
       * plainly at the sitting of 29 August 2026: `[DOM] Password field is not
       * contained in a form`. A handset then offers no `Valider` on its keyboard
       * and `Enter` does nothing, so a person typing four digits must reach for
       * the button every time , which is the friction this screen exists to
       * remove. `noValidate` because the guard is `inactive`, not the browser's
       * bubble, and the disabled rule is unchanged.
       */}
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          soumettre();
        }}
      >
        <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="connexion-pin">
          {t("client.connexion.champ")}
          <input
            id="connexion-pin"
            className={CHAMP}
            inputMode="numeric"
            type="password"
            // The handset's password manager is told what this is: the code that
            // opens this account, not a new one to invent.
            autoComplete="current-password"
            maxLength={4}
            value={pin}
            onChange={(event) => {
              setPin(event.target.value.replaceAll(/\D/gu, ""));
            }}
          />
        </label>
        {refusal === undefined ? null : (
          <p role="alert" className="text-sm text-rose-700">
            {refusal}
          </p>
        )}
        <Button type="submit" inactive={pin.length !== 4 || enCours}>
          {t("client.connexion.entrer")}
        </Button>
      </form>
      <button type="button" className={`text-sm ${BOUTON_TEXTE}`} onClick={oublie}>
        {t("client.connexion.oublie")}
      </button>
      <button type="button" className="rounded-lg text-sm text-gray-500 hover:bg-gray-100" onClick={pasVous}>
        {t("client.connexion.pasVous")}
      </button>
    </Card>
  );
}
